import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser, fetchUserAttributes , fetchAuthSession} from 'aws-amplify/auth';
import { listUserProfiles } from '../graphql/queries';
import { createUserProfile, updateUserProfile } from '../graphql/mutations';
import { FaUser, FaUniversity, FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import { updateUserSpecialty } from '../api/userProfileApi';
import OnboardingProgressBar from '../components/common/OnboardingProgressBar';

const AccountCreate = () => {
  const navigate = useNavigate();
  const client = generateClient();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    medicalUniversity: '',
    yearOfStudy: '',
  });
  const [err, setErr] = useState('');

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const loadUser = useCallback(async () => {
    setErr('');
    setLoading(true);

    for (let attempt = 1; attempt <= 12; attempt++) {
      try {
        await fetchAuthSession();           // forces session resolution
        const u = await getCurrentUser();   // now should succeed
        const attrs = await fetchUserAttributes();

        // Check if existing profile is complete
        const { data } = await client.graphql({
          query: listUserProfiles,
          variables: { filter: { owner: { eq: u.username } } },
          authMode: 'userPool'
        });
        const existing = data?.listUserProfiles?.items?.[0];

        // If profile exists and has required fields, skip account creation
        if (existing?.name && existing?.medicalUniversity && Number(existing?.yearInMedSchool) > 0) {
           navigate('/pathfinder-setup', { replace: true });
           return;
        }

        setUser({ ...u, attributes: attrs || {} });
        setForm((prev) => ({
          ...prev,
          fullName: existing?.name || attrs?.name || attrs?.given_name || prev.fullName,
          medicalUniversity: existing?.medicalUniversity || prev.medicalUniversity,
          yearOfStudy: existing?.yearInMedSchool || prev.yearOfStudy,
        }));

        setLoading(false);
        return;
      } catch (e) {
        if (attempt === 12) {
          setErr('Authentication required. Please return to Login and try again.');
          setLoading(false);
          return;
        }
        await sleep(250);
      }
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.fullName?.trim() || !form.medicalUniversity?.trim() || !form.yearOfStudy?.trim()) {
      setErr('Please complete all required fields.');
      return false;
    }
    const yr = Number(form.yearOfStudy);
    if (!Number.isInteger(yr) || yr <= 0) {
      setErr('Year of Study must be a valid number.');
      return false;
    }
    setErr('');
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !user?.username) return;
    setSaving(true);
    try {
      // Capture pending specialty early to pass to setup
      const pendingSpecialty = localStorage.getItem('medmap.pendingSpecialty') || '';

      // find existing profile by owner
      const { data } = await client.graphql({
        query: listUserProfiles,
        variables: { filter: { owner: { eq: user.username } } },
        authMode: 'userPool',
      });
      const existing = data?.listUserProfiles?.items?.[0];

      const inputCommon = {
        name: form.fullName.trim(),
        email: user.attributes?.email || '',
        username: user.username,
        yearInMedSchool: Number(form.yearOfStudy),
        medicalUniversity: form.medicalUniversity.trim(),
        demographics: '',
        owner: user.username,
      };

      let profileId;
      if (existing) {
        const res = await client.graphql({
          query: updateUserProfile,
          variables: { input: { id: existing.id, ...inputCommon } },
          authMode: 'userPool',
        });
        profileId = res?.data?.updateUserProfile?.id || existing.id;
      } else {
        const res = await client.graphql({
          query: createUserProfile,
          variables: { input: inputCommon },
          authMode: 'userPool',
        });
        profileId = res?.data?.createUserProfile?.id;
      }

      // If a pending specialty was selected pre-login, save it now
      try {
        if (pendingSpecialty && profileId) {
          await updateUserSpecialty(profileId, pendingSpecialty);
          localStorage.removeItem('medmap.pendingSpecialty');
        }
      } catch (e) {
        console.warn('Failed to apply pending specialty after profile creation:', e);
      }

      // Proceed to setup with selected specialty resolved
      const selectedForSetup = pendingSpecialty || existing?.chosenSpecialty || '';
      // navigate('/pathfinder-setup', { replace: true, state: { selectedSpecialty: selectedForSetup } });
      // Force a single full refresh when leaving the create account page so Header picks up the new profile
      window.location.replace('/pathfinder-setup');
    } catch (e) {
      setErr(e?.errors?.[0]?.message || e?.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ac-create-root">
        <div className="ac-header-spacer" aria-hidden="true" />
        <OnboardingProgressBar currentStep={2} />
        <div className="ac-create-inner">
          <div className="ac-create-card loading">
            <div className="ac-create-spinner" aria-hidden="true" />
            <p>Preparing your account…</p>
          </div>
        </div>
        <style jsx>{`
          .ac-create-root { background: radial-gradient(circle at 25% 18%, #eef6ff 0%, #ffffff 68%); min-height: 100vh; }
          .ac-header-spacer { height: calc(var(--header-height, 64px) + 0.75rem); }
          .ac-create-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:2rem; text-align:center; box-shadow:0 1px 3px rgba(0,0,0,0.06); }
          .ac-create-spinner { width:40px; height:40px; border:4px solid rgba(0,0,0,.08); border-top-color:#2563eb; border-radius:50%; animation:acspin 1s linear infinite; margin: 0 auto 1rem; }
          @keyframes acspin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <div className="ac-create-root">
      <div className="ac-header-spacer" aria-hidden="true" />
      <OnboardingProgressBar currentStep={2} />

      <section className="ac-create-hero">
        <div className="ac-create-hero-inner">
          <h1 className="ac-create-title">
            Complete your <span className="ac-create-gradient">MedMap Account</span>
          </h1>
          <p className="ac-create-sub">A few details to tailor Pathfinder to your training journey.</p>
        </div>
      </section>

      <div className="ac-create-inner">
        <form className="ac-create-form" onSubmit={onSubmit} noValidate>
          <div className="ac-create-grid">
            <label className="ac-create-field">
              <span className="ac-create-label">Full Name<span className="ac-create-req">*</span></span>
              <div className="ac-create-inputwrap">
                <FaUser className="ac-create-ico" />
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  placeholder="e.g., Dr. Jane Doe"
                  required
                />
              </div>
            </label>

            <label className="ac-create-field">
              <span className="ac-create-label">Medical University<span className="ac-create-req">*</span></span>
              <div className="ac-create-inputwrap">
                <FaUniversity className="ac-create-ico" />
                <input
                  type="text"
                  name="medicalUniversity"
                  value={form.medicalUniversity}
                  onChange={handleChange}
                  placeholder="e.g., University College London"
                  required
                />
              </div>
            </label>

            <label className="ac-create-field">
              <span className="ac-create-label">Year of Study<span className="ac-create-req">*</span></span>
              <div className="ac-create-inputwrap">
                <FaCalendarAlt className="ac-create-ico" />
                <input
                  type="number"
                  name="yearOfStudy"
                  value={form.yearOfStudy}
                  onChange={handleChange}
                  placeholder="e.g., 4"
                  min="1"
                  required
                />
              </div>
            </label>
          </div>

          {err && <div className="ac-create-error" role="alert">{err}</div>}

          <div className="ac-create-actions">
            <button className="ac-create-submit" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save and continue'} <FaArrowRight aria-hidden="true" />
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        :root {
          --ac-blue: #2563eb;
          --ac-blue-600: #2563eb;
          --ac-blue-700: #1d4ed8;
          --ac-bg: #f8fafc;
          --ac-card: #ffffff;
          --ac-border: #e2e8f0;
          --ac-text: #0f172a;
          --ac-muted: #64748b;
          --ac-shadow: 0 4px 14px -6px rgba(15,23,42,.08);
          --ac-shadow-lg: 0 18px 42px -16px rgba(15,23,42,.25);
          --ac-radius: 16px;
        }

        .ac-create-root { background: radial-gradient(circle at 25% 18%, #eef6ff 0%, #ffffff 68%); min-height: 100vh; }
        .ac-header-spacer { height: calc(var(--header-height, 64px) + 0.75rem); }
        .ac-create-hero { padding: clamp(4.5rem, 9vh, 6.5rem) 1.5rem 1.5rem; border-bottom: 1px solid var(--ac-border); }
        .ac-create-hero-inner { width: min(92%, 960px); margin: 0 auto; text-align: center; }
        .ac-create-title { margin: 0 0 .8rem; font-size: clamp(2rem, 4vw, 2.75rem); font-weight: 800; color: var(--ac-text); letter-spacing: -0.03em; }
        .ac-create-gradient { background: linear-gradient(90deg,#2563eb,#3b82f6,#6366f1); -webkit-background-clip:text; background-clip:text; color: transparent; }
        .ac-create-sub { margin: 0 auto; max-width: 62ch; color: var(--ac-muted); font-size: clamp(.98rem,1.05vw,1.1rem); }

        .ac-create-inner { width: min(92%, 960px); margin: 0 auto; padding: 1.5rem 1.5rem 3rem; }
        .ac-create-form { background: var(--ac-card); border: 1px solid var(--ac-border); border-radius: var(--ac-radius); padding: clamp(1.25rem, 2vw, 1.75rem); box-shadow: var(--ac-shadow); }
        .ac-create-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 860px) { .ac-create-grid { grid-template-columns: 1fr; } }

        .ac-create-field { display: grid; gap: .5rem; }
        .ac-create-label { font-weight: 700; color: var(--ac-text); font-size: .95rem; letter-spacing: .2px; }
        .ac-create-req { color: var(--ac-blue); margin-left: .25rem; }

        .ac-create-inputwrap { position: relative; display: flex; align-items: center; }
        .ac-create-ico { position: absolute; left: 0.9rem; color: #94a3b8; pointer-events: none; }
        .ac-create-inputwrap input {
          width: 100%; padding: .85rem .95rem .85rem 2.5rem; border-radius: 12px;
          border: 1px solid var(--ac-border); font-size: 1rem; color: var(--ac-text);
          background: #fff; transition: border-color .25s, box-shadow .25s;
        }
        .ac-create-inputwrap input:focus { outline: none; border-color: var(--ac-blue); box-shadow: 0 0 0 3px rgba(37,99,235,.15); }

        .ac-create-error {
          margin-top: 1rem; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca;
          padding: .75rem 1rem; border-radius: 12px; font-weight: 600;
        }

        .ac-create-actions { display: flex; justify-content: flex-end; margin-top: 1.25rem; }
        .ac-create-submit {
          display: inline-flex; align-items: center; gap: .5rem; padding: .9rem 1.4rem;
          border-radius: 999px; border: none; cursor: pointer; font-weight: 800; letter-spacing: .25px;
          background: linear-gradient(135deg,#2563eb,#3b82f6); color: #fff; box-shadow: var(--ac-shadow);
          transition: transform .45s cubic-bezier(.34,1.56,.64,1), box-shadow .45s cubic-bezier(.34,1.56,.64,1);
        }
        .ac-create-submit:hover { transform: translateY(-3px); box-shadow: var(--ac-shadow-lg); }
        .ac-create-submit:disabled { opacity: .75; cursor: not-allowed; transform: none; }

        /* @media (prefers-color-scheme: dark) {
          .ac-create-root { background: linear-gradient(180deg,#0f172a,#1e293b); }
          .ac-create-title { color: #f1f5f9; }
          .ac-create-sub { color: #94a3b8; }
          .ac-create-form { background:#1e293b; border-color:#334155; }
          .ac-create-inputwrap input { background:#0f172a; border-color:#334155; color:#e2e8f0; }
          .ac-create-inputwrap input:focus { box-shadow: 0 0 0 3px rgba(59,130,246,.2); }
        } */

        /* Focus styles */
        .ac-create-submit:focus-visible,
        .ac-create-inputwrap input:focus-visible {
          outline: 3px solid #93c5fd; outline-offset: 3px;
        }
      `}</style>
    </div>
  );
};

export default AccountCreate;
